import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AssignSafetyRoundTemplateRequest,
  CreateSafetyRoundTemplateRequest,
  SafetyRoundBusinessCandidate,
  SafetyRoundTemplateAssignment,
  SafetyRoundTemplateDetail,
  SafetyRoundTemplateStatus,
  SafetyRoundTemplateSummary,
  SaveSafetyRoundTemplateRequest,
} from '../safety-round.models';

/**
 * Vernerunde templates — Admin only, scoped by the backend to the caller's
 * application. Same v1 prefix as the business routes.
 */
@Injectable({ providedIn: 'root' })
export class FlexiSafetyRoundTemplateService {
  private readonly base = `${environment.BASE_URL}/flexi/safety-round-templates`;

  constructor(private http: HttpClient) {}

  list(status: SafetyRoundTemplateStatus | null = null): Observable<SafetyRoundTemplateSummary[]> {
    let params = new HttpParams();
    if (status !== null) {
      params = params.set('status', status);
    }
    return this.http
      .get<SafetyRoundTemplateSummary[] | null>(this.base, { params })
      .pipe(map(rows => rows ?? []));
  }

  get(id: number): Observable<SafetyRoundTemplateDetail> {
    return this.http.get<SafetyRoundTemplateDetail>(`${this.base}/${id}`);
  }

  /** Creates a draft and returns its id. */
  create(request: CreateSafetyRoundTemplateRequest): Observable<number> {
    return this.http.post<number>(this.base, request).pipe(map(id => Number(id)));
  }

  /** Replaces metadata and every item. 409 when the template is archived. */
  save(id: number, request: SaveSafetyRoundTemplateRequest): Observable<void> {
    return this.http.put<void>(`${this.base}/${id}`, request);
  }

  /** Version + 1 and Published. 400 when the template has no items. */
  publish(id: number): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/publish`, {});
  }

  /** Archived, which also clears IsDefaultForNewBusinesses. */
  archive(id: number): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/archive`, {});
  }

  /** 409 unless Published. */
  setDefaultForNew(id: number, isDefault: boolean): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/default-for-new`, { IsDefault: isDefault });
  }

  businesses(id: number): Observable<SafetyRoundTemplateAssignment[]> {
    return this.http
      .get<SafetyRoundTemplateAssignment[] | null>(`${this.base}/${id}/businesses`)
      .pipe(map(rows => rows ?? []));
  }

  /** At most 50 active businesses of the application, matching the search. */
  businessCandidates(id: number, search: string): Observable<SafetyRoundBusinessCandidate[]> {
    const params = new HttpParams().set('search', search);
    return this.http
      .get<SafetyRoundBusinessCandidate[] | null>(`${this.base}/${id}/business-candidates`, { params })
      .pipe(map(rows => rows ?? []));
  }

  /** 409 unless Published. */
  assign(id: number, request: AssignSafetyRoundTemplateRequest): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/businesses`, request);
  }

  unassign(id: number, businessId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}/businesses/${businessId}`);
  }
}
